/**
 * useTTS Hook - Centralized Text-to-Speech with Intelligent Fallback
 * 
 * Features:
 * - Backend TTS (ElevenLabs → OpenAI) with caching
 * - Browser TTS fallback if backend unavailable
 * - Rate limiting & error handling
 * - Multi-language support
 * - INTEGRATION: Pauses global audio player during speech
 * 
 * Usage:
 * const { speak, stop, isSpeaking } = useTTS({ 
 *   language: 'de', 
 *   deviceId: 'xxx',
 *   audioPlayer: useAudioPlayer() // Optional: for pause/resume
 * });
 * speak('Hallo Welt');
 */
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

export function useTTS({ 
  language = 'de', 
  deviceId = 'default', 
  mode = 'auto', 
  seniorId = null,
  audioPlayer = null  // Optional: global audio player for pause/resume
} = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const wasPlayingBeforeTTS = useRef(false);

  /**
   * Stop current speech (backend or browser)
   */
  const stop = useCallback(() => {
    // Stop backend audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Stop browser TTS
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(false);
    setIsLoading(false);
    
    // Resume audio player if it was playing
    if (audioPlayer && wasPlayingBeforeTTS.current) {
      console.log('[TTS] Resuming audio player');
      audioPlayer.resume();
      wasPlayingBeforeTTS.current = false;
    }
  }, [audioPlayer]);

  /**
   * Fallback to browser TTS
   */
  const speakWithBrowser = useCallback((text) => {
    if (!window.speechSynthesis) {
      toast.error('Browser-TTS nicht verfügbar');
      return;
    }

    try {
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Language mapping
      const langMap = {
        de: 'de-DE',
        tr: 'tr-TR',
        en: 'en-US',
        ar: 'ar-SA'
      };
      utterance.lang = langMap[language] || 'de-DE';
      
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onstart = () => {
        setIsSpeaking(true);
        console.log('[TTS Browser] Started');
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        console.log('[TTS Browser] Ended');
        
        // Resume audio player if it was playing
        if (audioPlayer && wasPlayingBeforeTTS.current) {
          console.log('[TTS] Resuming audio player after browser TTS');
          audioPlayer.resume();
          wasPlayingBeforeTTS.current = false;
        }
      };

      utterance.onerror = (e) => {
        console.error('[TTS Browser] Error:', e);
        setIsSpeaking(false);
        toast.error('Vorlesen fehlgeschlagen');
      };

      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.error('[TTS Browser] Failed:', err);
      toast.error('Browser-TTS fehlgeschlagen');
    }
  }, [language]);

  /**
   * Speak text using backend TTS with browser fallback
   */
  const speak = useCallback(async (text) => {
    if (!text || !text.trim()) {
      toast.error('Kein Text zum Vorlesen');
      return;
    }

    // Stop any current speech
    stop();
    
    // Pause audio player if playing
    if (audioPlayer && audioPlayer.isPlaying) {
      console.log('[TTS] Pausing audio player for TTS');
      wasPlayingBeforeTTS.current = true;
      audioPlayer.pause();
    }

    // Truncate if too long
    const maxChars = 800;
    let textToSpeak = text;
    if (text.length > maxChars) {
      textToSpeak = text.substring(0, maxChars) + '...';
      console.warn(`[TTS] Text truncated to ${maxChars} chars`);
    }

    try {
      setIsLoading(true);
      console.log(`[TTS Backend] Requesting: lang=${language}, len=${textToSpeak.length}, device=${deviceId}`);

      // Try backend TTS first (only if mode allows)
      if (mode === 'browser-only') {
        throw new Error('Browser-only mode');
      }

      const response = await axios.post(
        `${API}/api/tts`,
        {
          text: textToSpeak,
          language,
          device_id: deviceId,
          senior_id: seniorId
        },
        { timeout: 10000 }
      );

      if (response.data && response.data.audio_url) {
        console.log(`[TTS Backend] Success: provider=${response.data.provider}, cached=${response.data.cached}`);

        // Play audio
        const audioUrl = `${API}${response.data.audio_url}`;
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onplay = () => {
          setIsSpeaking(true);
          setIsLoading(false);
          console.log('[TTS Backend] Playing audio');
        };

        audio.onended = () => {
          setIsSpeaking(false);
          audioRef.current = null;
          console.log('[TTS Backend] Audio ended');
          
          // Resume audio player if it was playing
          if (audioPlayer && wasPlayingBeforeTTS.current) {
            console.log('[TTS] Resuming audio player after TTS');
            audioPlayer.resume();
            wasPlayingBeforeTTS.current = false;
          }
        };

        audio.onerror = (e) => {
          console.error('[TTS Backend] Audio playback error:', e);
          setIsSpeaking(false);
          setIsLoading(false);
          audioRef.current = null;
          
          // Fallback to browser TTS
          console.log('[TTS] Fallback to browser TTS');
          speakWithBrowser(textToSpeak);
        };

        await audio.play();
      } else {
        throw new Error('No audio_url in response');
      }
    } catch (err) {
      console.error('[TTS Backend] Failed:', err.response?.data || err.message);
      setIsLoading(false);
      setError(err.response?.data?.detail || err.message);

      // Fallback to browser TTS (if mode allows)
      if (mode === 'auto') {
        console.log('[TTS] Backend unavailable, using browser TTS');
        
        // Show subtle notification
        const statusCode = err.response?.status;
        if (statusCode === 503 || statusCode === 403) {
          toast('Verwende lokale Sprachausgabe', { icon: '🔊', duration: 3000 });
        } else if (statusCode === 429) {
          toast('Zu viele Anfragen. Bitte warten...', { icon: '⏱️' });
        }

        speakWithBrowser(textToSpeak);
      } else if (mode === 'backend-only') {
        toast.error('Sprachdienst nicht verfügbar');
      }
    }
  }, [language, deviceId, stop, speakWithBrowser, audioPlayer]);

  return {
    speak,
    stop,
    isSpeaking,
    isLoading,
    error
  };
}

export default useTTS;
